// A challenge at /c/<code>. Someone not in it yet gets the invite: who sent it and their score,
// a name to play under, and Accept. Players in it get the winner, the standings, head-to-head
// records, both boards row by row once they've finished, and one chat for everyone in the group.
import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import type { Challenge, ChallengePlay, ChallengePlayer, EditionInfo, Me } from '@wbwl/contract'
import { NameBadge } from '../components/Badge.tsx'
import { andList, ChallengeSheet, challengeUrl, FormLine, matchCount, PatternGrid, progressText, recordText, standingsOf } from '../components/challenge-ui.tsx'
import { WordTiles } from '../components/game-ui.tsx'
import { HowTo } from '../components/HowTo.tsx'
import { Icon } from '../components/Icon.tsx'
import { Avatar, ErrorNote, Loading, Logo, NotFound, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useChallenge, useChallengeThread, useEdition, useMe, usePlay } from '../lib/data.ts'
import { fmt, longDate, returnAfterSignIn, shortDate } from '../lib/game.ts'
import { copyText, nativeShare } from '../lib/share.ts'
import { invalidate, setResource } from '../lib/store.ts'
import { CommentRow } from './Discussion.tsx'

export default function ChallengePage() {
  const code = useParams().code ?? ''
  const q = useChallenge(code)
  const ed = useEdition(q.data?.edition ?? null)
  const me = useMe().data
  // Opening it marks the edition's results seen, which lifts their hold on records and the dot.
  const seen = q.data?.me === 'joined' ? `${q.data.code}:${q.data.players.filter((p) => p.fresh).length}` : null
  useEffect(() => { if (seen) invalidate('mychallenges', 'rivals', 'rivalry:') }, [seen])

  if (q.error) {
    if (q.error instanceof ApiFailure && q.error.status === 404) return <NotFound />
    return <div className="page"><ErrorNote error={q.error} retry={() => q.reload()} /></div>
  }
  if (ed.error) return <div className="page"><ErrorNote error={ed.error} retry={() => ed.reload()} /></div>
  if (!q.data || !ed.data || !me) return <div className="page"><Loading label="Opening the challenge…" /></div>
  return q.data.me === 'joined'
    ? <ChallengeView ch={q.data} info={ed.data} me={me} />
    : <Invite ch={q.data} info={ed.data} me={me} />
}

// ---------- the invite ----------

function Invite({ ch, info, me }: { ch: Challenge; info: EditionInfo; me: Me }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [name, setName] = useState(me.displayName)
  const [busy, setBusy] = useState(false)
  const [howto, setHowto] = useState(false)
  const myPlay = usePlay(ch.finished ? ch.edition : null).data
  const creator = ch.players.find((p) => p.creator)
  const also = ch.players.filter((p) => p.status === 'joined' && !p.creator && !p.mine)
  const closed = !ch.open && ch.me === 'none'
  const expired = Date.parse(ch.expiresAt) <= Date.now()

  async function accept(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const wanted = name.trim().replace(/\s+/g, ' ')
      if (me.kind === 'guest' && wanted && wanted !== me.displayName) {
        setResource('me', await api.patch<Me>('/me', { displayName: wanted }))
        invalidate('board:', 'thread:', 'chat:')
      }
      const joined = await api.post<Challenge>(`/challenges/${ch.code}/join`)
      setResource(`challenge:${ch.code}`, joined)
      invalidate('edchallenges:', 'mychallenges')
      if (!joined.finished) navigate(info.isToday ? '/play' : `/play/${info.number}`)
    } catch (err) {
      const b = err instanceof ApiFailure ? err.body : null
      toast(b?.error === 'invalid_name' ? '2–24 letters, numbers, spaces, dots, dashes or underscores' : b?.message || 'Couldn’t join that challenge')
    } finally {
      setBusy(false)
    }
  }
  async function decline() {
    try { await api.post(`/challenges/${ch.code}/decline`) } catch { /* it's gone either way */ }
    invalidate('edchallenges:', 'mychallenges', `challenge:${ch.code}`)
    navigate('/')
  }
  function signIn() {
    returnAfterSignIn(`/c/${ch.code}`)
    navigate('/signin')
  }

  return (
    <div className="page invite">
      <header className="today-top">
        <Link to="/" aria-label="Letters on Fire home"><Logo height={24} /></Link>
        <button type="button" className="ibtn" aria-label="How to play" onClick={() => setHowto(true)}><Icon name="help" /></button>
      </header>
      <div className="masthead">
        <div className="double-rule" />
        <div className="eyebrow dateline"><span>No. {info.number}</span><span>{longDate(info.date)}</span></div>
        <div className="rule ink" />
      </div>

      <h1 className="h-serif invite-h1">{ch.creator.displayName} <em className="burn-em">challenged</em> you.</h1>
      <p className="muted lede">Play {info.isToday ? 'today’s puzzle' : `No. ${info.number}`}, then compare your boards word for word.</p>

      {creator?.done && (
        <div className="card challenger">
          <div className="top">
            <Avatar id={creator.id} name={creator.displayName} src={creator.avatar} size={48} />
            <div>
              <span className="eyebrow">{creator.displayName} made the</span>
              <span className="ch-rk">{creator.rankName}</span>
              <span className="ch-sc">{fmt(creator.total!)}</span>
            </div>
            {creator.grid && <PatternGrid grid={creator.grid} m={9} />}
          </div>
          <div className="hide"><Icon name="eyeoff" size={17} />{creator.displayName}’s words stay hidden until you finish.</div>
          {ch.riddle && <div className="riddle"><Icon name="feather" size={17} /><span>{creator.displayName}’s ashes <b>{ch.riddle.ashes.join(' ')}</b> made a {ch.riddle.points}-point phoenix.</span></div>}
        </div>
      )}
      {also.length > 0 && (
        <div className="also">
          <div className="stack">{also.slice(0, 4).map((p) => <Avatar key={p.id} id={p.id} name={p.displayName} src={p.avatar} size={28} />)}</div>
          <span>{andList(also.slice(0, 3).map((p) => p.displayName))}{also.length > 3 ? ` and ${also.length - 3} more` : ''} {also.length === 1 ? 'is' : 'are'} in too.</span>
        </div>
      )}

      {closed ? (
        <div className="card locked">
          <Icon name="clock" size={22} />
          <div><b>{expired ? 'This challenge has closed.' : 'This challenge is full.'}</b><p className="muted small">{ch.finished ? `You’ve played No. ${info.number} already.` : info.isToday ? 'Today’s puzzle is still yours to play.' : 'You can still play the edition on your own.'}</p></div>
          {ch.finished
            ? <Link className="btn ink sm" to={`/results/${info.number}`}>Your results</Link>
            : <Link className="btn ink sm" to={info.isToday ? '/play' : `/play/${info.number}`}>Play No. {info.number}</Link>}
        </div>
      ) : (
        <form onSubmit={accept}>
          {me.kind === 'guest' && (
            <div className="field">
              <label htmlFor="who">Your name <span>· {andList([ch.creator.displayName, ...also.slice(0, 2).map((p) => p.displayName)])} will see it</span></label>
              <input id="who" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoComplete="nickname" />
            </div>
          )}
          {ch.finished && myPlay && <p className="muted small already">You’ve already played No. {info.number}: {myPlay.standing.name}, {fmt(myPlay.scores.total)}.</p>}
          <button type="submit" className="btn ink block" disabled={busy}>{ch.finished ? 'Accept and compare' : `Accept and play No. ${info.number}`}</button>
          <div className="invite-links">
            {me.kind === 'guest' && <button type="button" className="btn plain" onClick={signIn}>Have an account? Sign in so the record follows you</button>}
            {ch.me === 'invited' && <button type="button" className="btn plain" onClick={decline}>Not today</button>}
            <button type="button" className="btn plain" onClick={() => setHowto(true)}>New here? How to play</button>
          </div>
        </form>
      )}
      <HowTo open={howto} onClose={() => setHowto(false)} />
    </div>
  )
}

// ---------- the challenge ----------

function ChallengeView({ ch, info, me }: { ch: Challenge; info: EditionInfo; me: Me }) {
  const toast = useToast()
  const navigate = useNavigate()
  const entry = useLocation().key === 'default'
  const [sheet, setSheet] = useState<Challenge | null>(null)
  const [vsId, setVsId] = useState<string | null>(null)
  const { joined, done, pending, winners, mine } = standingsOf(ch)
  const others = joined.filter((p) => !p.mine)
  const comparable = others.filter((p) => p.play)
  const vs = comparable.find((p) => p.id === vsId) ?? comparable[0] ?? null
  const rivals = others.filter((p) => p.record && matchCount(p.record) > 0)
  const own = ch.creator.id === me.id
  const playPath = info.isToday ? '/play' : `/play/${info.number}`
  const position = (p: ChallengePlayer) => 1 + done.filter((o) => (o.total ?? 0) > (p.total ?? 0)).length

  async function shareResult() {
    const lines = done.map((p) => `${position(p)}. ${p.displayName} ${fmt(p.total!)}`).join('\n')
    const text = `Letters on Fire No. ${info.number} challenge\n${lines}\n${challengeUrl(ch.code)}`
    if ((await nativeShare(text)) === 'unavailable') toast(await copyText(text) ? <><Icon name="check" />Copied — paste it anywhere</> : 'Couldn’t copy that')
  }

  return (
    <div className="page challenge">
      <div className="topbar">
        <button type="button" className="ibtn" aria-label="Back" onClick={() => (entry ? navigate('/rivals') : navigate(-1))}><Icon name="back" /></button>
        <div className="title">
          <b>{own ? 'Your challenge' : `${ch.creator.displayName}’s challenge`} · No. {info.number}</b>
          <small>{shortDate(info.date)} · {joined.length} PLAYER{joined.length === 1 ? '' : 'S'}</small>
        </div>
        {ch.open && <button type="button" className="ibtn" aria-label="Invite more friends" onClick={() => setSheet(ch)}><Icon name="share" /></button>}
      </div>

      <section className="ch-hero">
        <div className="double-rule" />
        <Hero ch={ch} />
        {!mine?.done && <Link className="btn ink block ch-play" to={playPath}>Play No. {info.number}</Link>}
        {mine?.done && joined.length === 1 && <button type="button" className="btn ink block ch-play" onClick={() => setSheet(ch)}><Icon name="share" />Send the link</button>}
      </section>

      <section className="card standings" aria-label="Standings">
        {ch.players.map((p) => {
          const cls = `st${p.done && winners.includes(p) && pending.length === 0 && done.length > 1 ? ' win' : ''}`
          const cells = (
            <>
              <span className="pos">{p.done ? position(p) : ''}</span>
              <Avatar id={p.id} name={p.displayName} src={p.avatar} />
              <div className="who">
                <div className="named"><b className="ellipsis">{p.displayName}{p.mine && <span className="you-tag">you</span>}</b><NameBadge badge={p.badge} /></div>
                <span className={p.done ? '' : 'st-progress'}>{progressText(p)}</span>
              </div>
              {p.grid ? <PatternGrid grid={p.grid} /> : <span />}
              <b className={`pts${p.done ? '' : ' faint'}`}>{p.done ? fmt(p.total!) : '—'}</b>
            </>
          )
          // A member's row opens their profile; a guest has none.
          return p.profile ? <Link key={p.id} className={cls} to={`/p/${p.profile}`}>{cells}</Link> : <div key={p.id} className={cls}>{cells}</div>
        })}
      </section>

      {rivals.length > 0 && (
        <section className="h2h">
          <div className="sec-head"><h3>Head-to-head</h3><span className="eyebrow">every challenge</span></div>
          {rivals.map((p) => {
            const rec = recordText(p.record!, p.displayName)
            return (
              <Link key={p.id} className="h2h-row" to={`/rivals/${p.id}`}>
                <Avatar id={p.id} name={p.displayName} src={p.avatar} />
                <div className="who"><b className="ellipsis">You vs {p.displayName}</b><FormLine form={p.record!.form} /></div>
                <div className="rec"><b>{rec.score}</b><span>{rec.lead}</span></div>
                <Icon name="next" size={18} />
              </Link>
            )
          })}
        </section>
      )}

      {mine?.play && vs?.play && (
        <section className="compare">
          <div className="sec-head">
            <h3>Compare boards</h3>
            {comparable.length > 1 && (
              <div className="seg mini" role="group" aria-label="Compare with">
                {comparable.map((p) => <button key={p.id} type="button" className={p === vs ? 'on' : ''} onClick={() => setVsId(p.id)}>{p.displayName}</button>)}
              </div>
            )}
          </div>
          <Duel info={info} me={mine} mine={mine.play} them={vs} theirs={vs.play} />
        </section>
      )}
      {mine?.done && others.length > 0 && !vs && (
        <p className="muted small center ch-wait">{andList(pending.map((p) => p.displayName))}’s words show up here when they finish.</p>
      )}

      {mine?.done && (
        <section className="res-actions">
          <button type="button" className="btn ghost" onClick={shareResult} disabled={done.length < 2}><Icon name="share" />Share result</button>
          <button type="button" className="btn ghost" onClick={() => setSheet(ch)} disabled={!ch.open}><Icon name="link" />Invite more</button>
        </section>
      )}

      <Talk key={ch.code} ch={ch} />
      <ChallengeSheet challenge={sheet} me={me} onClose={() => setSheet(null)} onChange={setSheet} />
    </div>
  )
}

/** The headline: the winner, who you're waiting on, or your turn. */
function Hero({ ch }: { ch: Challenge }) {
  const { joined, done, pending, best, winners, mine } = standingsOf(ch)
  const leader = winners.length === 1 ? (winners[0].mine ? 'you lead' : `${winners[0].displayName} leads`) : 'tied at the top'
  if (!mine?.done) {
    return (
      <>
        <div className="eyebrow dateline center">No. {ch.edition} · your move</div>
        <div className="rule ink" />
        <div className="h-serif ch-name">Your turn.</div>
        <div className="ch-by">{best !== null ? <>{winners.map((p) => p.displayName).join(' and ')} {winners.length === 1 ? 'leads' : 'lead'} with <b>{fmt(best)}</b>. </> : null}Their words stay hidden until you finish.</div>
      </>
    )
  }
  if (joined.length === 1) {
    return (
      <>
        <div className="eyebrow dateline center">Nobody’s joined yet</div>
        <div className="rule ink" />
        <div className="h-serif ch-name">Who’s next?</div>
        <div className="ch-by">Send the link to one friend or a group chat.</div>
      </>
    )
  }
  if (pending.length > 0) {
    return (
      <>
        <div className="eyebrow dateline center">Waiting on</div>
        <div className="rule ink" />
        <div className="h-serif ch-name">{andList(pending.map((p) => p.displayName))}</div>
        <div className="ch-by">{done.length} of {joined.length} finished · {leader} with <b>{fmt(best!)}</b></div>
      </>
    )
  }
  if (winners.length > 1) {
    return (
      <>
        <div className="eyebrow dateline center">Everyone finished · a tie between</div>
        <div className="rule ink" />
        <div className="h-serif ch-name">{andList(winners.map((p) => (p.mine ? 'You' : p.displayName)))}</div>
        <div className="ch-by">All on <b>{fmt(best!)}</b></div>
      </>
    )
  }
  const winner = winners[0]
  const second = done.filter((p) => p !== winner).reduce((a, b) => ((b.total ?? 0) > (a.total ?? 0) ? b : a))
  return (
    <>
      <div className="eyebrow dateline center">Everyone finished · the winner is</div>
      <div className="rule ink" />
      <div className="ch-winner"><Icon name="crown" className="crown-gold" /><Avatar id={winner.id} name={winner.displayName} src={winner.avatar} size={64} /></div>
      <div className="h-serif ch-name">{winner.mine ? 'You' : winner.displayName}</div>
      <div className="ch-by">
        {winner.mine
          ? <><b>{fmt(winner.total!)}</b> to {second.displayName}’s <b>{fmt(second.total!)}</b> · by {fmt(winner.total! - second.total!)} points</>
          : <><b>{fmt(winner.total!)}</b> to your <b>{fmt(mine.total!)}</b> · by {fmt(winner.total! - mine.total!)} points</>}
      </div>
    </>
  )
}

// ---------- comparing two boards ----------

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six']
const rowsWon = (n: number) => `${NUMBER_WORDS[n] ?? n} row${n === 1 ? '' : 's'}`

interface Turn { label: string; a: string | null; b: string | null; as: number; bs: number }

/** Where the winner gained most, and who won what. */
function turningPoint(mine: ChallengePlay, theirs: ChallengePlay, name: string): ReactNode {
  const a = mine.scores.total
  const b = theirs.scores.total
  if (a === b) return <><b>Dead even.</b> You both finished on {fmt(a)}.</>
  const turns: Turn[] = []
  const n = Math.max(mine.rows.length, theirs.rows.length)
  for (let r = 0; r < n; r++) turns.push({ label: `Row ${r + 1}`, a: mine.rows[r] ?? null, b: theirs.rows[r] ?? null, as: mine.scores.rows[r]?.total ?? 0, bs: theirs.scores.rows[r]?.total ?? 0 })
  const phx: Turn = { label: 'The phoenix', a: mine.phoenix, b: theirs.phoenix, as: mine.scores.phoenix?.total ?? 0, bs: theirs.scores.phoenix?.total ?? 0 }
  const youWon = a > b
  const swing = (t: Turn) => (youWon ? t.as - t.bs : t.bs - t.as)
  const top = [...turns, phx].reduce((x, y) => (swing(y) > swing(x) ? y : x))
  const [wWord, wScore, lWord, lScore] = youWon ? [top.a, top.as, top.b, top.bs] : [top.b, top.bs, top.a, top.as]
  const winnerOf = youWon ? 'Your' : `${name}’s`
  const loserOf = youWon ? `${name}’s` : 'your'
  const what = lWord ? `${winnerOf} ${wWord} scored ${wScore} to ${loserOf} ${lWord}’s ${lScore}.` : `${winnerOf} ${wWord} scored ${wScore}, with nothing to answer it.`

  const myRows = turns.filter((t) => t.as > t.bs).length
  const theirRows = turns.filter((t) => t.bs > t.as).length
  const clause = (who: string, rows: number, phoenix: boolean) => `${who} won ${rowsWon(rows)}${phoenix ? ' and the phoenix' : ''}`
  const mineClause = clause('you', myRows, phx.as > phx.bs)
  const theirClause = clause(name, theirRows, phx.bs > phx.as)
  const margin = fmt(Math.abs(a - b))
  const [first, last] = youWon ? [theirClause, mineClause] : [mineClause, theirClause]
  const summary = `${first[0].toUpperCase()}${first.slice(1)}; ${last}, and the challenge by ${margin}.`
  return <><b>{top.label}.</b> {what} {summary}</>
}

function Duel({ info, me, mine, them, theirs }: { info: EditionInfo; me: ChallengePlayer; mine: ChallengePlay; them: ChallengePlayer; theirs: ChallengePlay }) {
  const n = Math.max(mine.rows.length, theirs.rows.length)
  // Two five-letter words side by side, a little smaller on the narrowest phones.
  const size = window.innerWidth < 370 ? 18 : 21
  const cls = (x: number, y: number) => (x > y ? ' win' : x === y ? ' tie' : '')
  const word = (w: string | null | undefined, row?: number, k?: string) =>
    w ? <WordTiles edition={info} word={w} row={row} size={size} gap={2} k={k} /> : <span className="dw-empty" />
  const pa = mine.scores.phoenix?.total ?? 0
  const pb = theirs.scores.phoenix?.total ?? 0
  return (
    <div className="duel" style={{ '--dw': `${5 * size + 8}px` } as CSSProperties}>
      <div className="duel-head">
        <div className="dh"><Avatar id={me.id} name={me.displayName} src={me.avatar} size={24} />You</div>
        <div className="dh r"><span className="ellipsis">{them.displayName}</span><Avatar id={them.id} name={them.displayName} src={them.avatar} size={24} /></div>
      </div>
      {Array.from({ length: n }, (_, r) => {
        const a = mine.scores.rows[r]?.total
        const b = theirs.scores.rows[r]?.total
        return (
          <div className="duel-row" key={r}>
            {word(mine.rows[r], r)}
            <span className={`ds${a !== undefined && b !== undefined ? cls(a, b) : a !== undefined ? ' win' : ''}`}>{a ?? '—'}</span>
            <span className="dn">{r + 1}</span>
            <span className={`ds r${a !== undefined && b !== undefined ? cls(b, a) : b !== undefined ? ' win' : ''}`}>{b ?? '—'}</span>
            {word(theirs.rows[r], r)}
          </div>
        )
      })}
      <div className="duel-row">
        {mine.phoenix ? word(mine.phoenix, undefined, 'ash') : <span className="dw-empty faint mono small">{mine.phoenixResult === 'no_word' ? 'no phoenix' : mine.phoenixResult === 'day_over' ? 'day ended' : 'skipped'}</span>}
        <span className={`ds${cls(pa, pb)}`}>{pa}</span>
        <span className="dn" aria-label="Phoenix"><Icon name="feather" size={14} /></span>
        <span className={`ds r${cls(pb, pa)}`}>{pb}</span>
        {theirs.phoenix ? word(theirs.phoenix, undefined, 'ash') : <span className="dw-empty faint mono small r">{theirs.phoenixResult === 'no_word' ? 'no phoenix' : theirs.phoenixResult === 'day_over' ? 'day ended' : 'skipped'}</span>}
      </div>
      <div className="duel-total">
        <b className={mine.scores.total < theirs.scores.total ? 'lose' : ''}>{mine.scores.total > theirs.scores.total && <Icon name="crown" className="crown-gold" />}{fmt(mine.scores.total)}</b>
        <span className="eyebrow ink">Total</span>
        <b className={`r${theirs.scores.total < mine.scores.total ? ' lose' : ''}`}>{theirs.scores.total > mine.scores.total && <Icon name="crown" className="crown-gold" />}{fmt(theirs.scores.total)}</b>
      </div>
      <div className="card turning">
        <span className="eyebrow burn-ink">{mine.scores.total === theirs.scores.total ? 'All square' : 'The turning point'}</span>
        <p>{turningPoint(mine, theirs, them.displayName)}</p>
      </div>
    </div>
  )
}

// ---------- the chat ----------

/**
 * The group's chat: one thread for everyone in this challenge and in the edition's challenges
 * linked to it, open once you've finished.
 */
function Talk({ ch }: { ch: Challenge }) {
  const thread = useChallengeThread(ch.code)
  const me = useMe().data
  const toast = useToast()
  const [body, setBody] = useState('')
  const t = thread.data

  async function post(e: FormEvent) {
    e.preventDefault()
    const text = body.trim()
    if (!text) return
    try {
      await api.post(`/challenges/${ch.code}/comments`, { body: text })
      setBody('')
      invalidate('chat:')
    } catch (err) {
      toast(err instanceof ApiFailure && err.body.error === 'rate_limited' ? 'You’re posting fast — try again in a minute' : 'Couldn’t post that')
    }
  }
  async function remove(id: string) {
    try { await api.del(`/challenge-comments/${id}`); invalidate('chat:') } catch { toast('Couldn’t delete that') }
  }

  const names = t?.players.filter((p) => p.id !== me?.id).map((p) => p.displayName) ?? []
  return (
    <section className="ch-talk discussion">
      <div className="sec-head"><h3>Talk it over</h3><span className="eyebrow">{t ? `${t.count} comment${t.count === 1 ? '' : 's'}` : ''}</span></div>
      {thread.error ? <ErrorNote error={thread.error} retry={() => thread.reload()} /> : !t ? null : t.locked ? (
        <div className="card locked">
          <Icon name="eyeoff" size={22} />
          <div><b>Finish No. {ch.edition} to join in.</b><p className="muted small">The chat is full of words, so it opens once your play is done.</p></div>
        </div>
      ) : (
        <>
          <div className="gate-note"><Icon name="lock" size={18} /><span className="muted small">{names.length <= 3 ? `Only ${andList(['you', ...names])} can read this.` : `Only the ${names.length + 1} players in this group can read this.`}</span></div>
          {t.comments.length === 0 && <p className="muted empty-note">No comments yet. Say how it went.</p>}
          {t.comments.map((c) => <CommentRow key={c.id} c={c} onDelete={() => remove(c.id)} />)}
          <div style={{ height: 96 }} />
          <form className="composer" onSubmit={post}>
            <div className="composer-row">
              <Avatar id={me?.id} name={me?.displayName} src={me?.avatar} size={32} />
              <input value={body} onChange={(e) => setBody(e.target.value)} maxLength={1000} placeholder={names.length ? `Say something to ${names.length > 2 ? `${names.slice(0, 2).join(', ')} and the rest` : andList(names)}…` : 'Say something…'} aria-label="Comment" />
              <button className="btn ink sm" type="submit" disabled={!body.trim()}>Post</button>
            </div>
          </form>
        </>
      )}
    </section>
  )
}
